<script>
  export let poster;
  export let isActive = false;
  export let direction = "current"; // 'current', 'next', 'prev'

  const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".m4v"];
  let mediaVideo;

  // Determine if we're on a mobile device
  let isMobile = false;

  $: mediaPath = `/images/apps/${poster.imagePath}`;
  $: normalizedMediaPath = poster.imagePath.toLowerCase();
  $: isVideo = videoExtensions.some((extension) =>
    normalizedMediaPath.endsWith(extension)
  );

  $: if (isVideo && mediaVideo) {
    if (isActive) {
      mediaVideo.play().catch(() => {});
    } else {
      mediaVideo.pause();
      mediaVideo.currentTime = 0;
    }
  }

  if (typeof window !== "undefined") {
    isMobile = window.innerWidth < 768;
    // Add resize listener
    const handleResize = () => {
      isMobile = window.innerWidth < 768;
    };
    window.addEventListener("resize", handleResize);
  }
</script>

<div
  class="absolute inset-0 transition-all duration-500 ease-out transform
  {isActive
    ? 'opacity-100 translate-x-0 z-10'
    : direction === 'next'
      ? 'opacity-0 translate-x-full z-0'
      : 'opacity-0 -translate-x-full z-0'}"
>
  <div
    class="w-full h-full flex flex-col md:flex-row items-center gap-4 md:gap-12"
  >
    <!-- Content section - stacks vertically on mobile -->
    <div class="w-full md:w-1/3 p-4 md:pr-8 z-20 text-center md:text-left">
      <div class="space-y-4 md:space-y-6">
        <h3
          class="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white"
        >
          {poster.text}
        </h3>
        <p
          class="text-base md:text-lg text-gray-600 dark:text-gray-400 leading-relaxed"
        >
          {poster.description}
        </p>

        {#if poster.status}
          <div
            class="inline-flex items-center gap-2 px-3 py-1 rounded-full
            bg-gradient-to-r from-blue-500/10 to-purple-500/10
            dark:from-blue-500/20 dark:to-purple-500/20
            border border-blue-500/20 dark:border-purple-500/20"
          >
            <span
              class="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 animate-pulse"
            ></span>
            <span
              class="text-sm font-medium bg-clip-text text-transparent
              bg-gradient-to-r from-blue-500 to-purple-500"
            >
              {poster.status}
            </span>
          </div>
        {/if}

        {#if poster.fancyTech}
          <div
            class="flex flex-wrap gap-2 md:gap-3 justify-center md:justify-start"
          >
            {#each poster.fancyTech as tech}
              <div
                class="px-3 md:px-4 py-1 md:py-1.5 text-xs md:text-sm font-medium
                bg-transparent
                border border-blue-400/20 dark:border-blue-400/20
                text-blue-400/90 dark:text-blue-400/90"
              >
                {tech}
              </div>
            {/each}
          </div>
        {/if}

        <div
          class="flex gap-3 md:gap-4 justify-center md:justify-start flex-wrap"
        >
          {#if poster.websiteLink}
            <a
              href={poster.websiteLink}
              target="_blank"
              rel="noopener noreferrer"
              class="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700
              text-white text-sm md:text-base rounded-lg transition-all flex items-center gap-2 shadow-lg shadow-violet-500/20"
            >
              <svg
                class="w-4 h-4 md:w-5 md:h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6" />
                <path d="M15 3h6v6" />
                <path d="M10 14L21 3" />
              </svg>
              <span>{poster.websiteLabel || "Website"}</span>
            </a>
          {/if}

          {#if poster.androidLink}
            <a
              href={poster.androidLink}
              target="_blank"
              rel="noopener noreferrer"
              class="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600
              text-white text-sm md:text-base rounded-lg transition-all flex items-center gap-2 shadow-lg shadow-blue-500/20"
            >
              <svg
                class="w-4 h-4 md:w-5 md:h-5"
                viewBox="0 0 505.145 505.145"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  d="M68.541,164.715h-1.294c-16.588,0-30.113,13.568-30.113,30.113v131.107 c0,16.61,13.525,30.134,30.113,30.134h1.316c16.588,0,30.113-13.568,30.113-30.134V194.827 C98.654,178.283,85.108,164.715,68.541,164.715z"
                />
                <path
                  d="M113.085,376.54c0,15.229,12.446,27.632,27.675,27.632h29.574v70.817 c0,16.631,13.568,30.156,30.113,30.156h1.294c16.61,0,30.156-13.546,30.156-30.156v-70.817h41.33v70.817 c0,16.631,13.611,30.156,30.156,30.156h1.273c16.609,0,30.134-13.546,30.134-30.156v-70.817h29.595 c15.207,0,27.654-12.403,27.654-27.632V169.525H113.085V376.54z"
                />
                <path
                  d="M322.041,43.983l23.491-36.26c1.51-2.287,0.841-5.414-1.467-6.903 c-2.286-1.51-5.414-0.884-6.903,1.467l-24.353,37.512c-18.27-7.485-38.676-11.691-60.226-11.691 c-21.571,0-41.934,4.206-60.247,11.691l-24.31-37.512c-1.489-2.351-4.638-2.977-6.925-1.467c-2.308,1.488-2.977,4.616-1.467,6.903 l23.512,36.26c-42.387,20.773-70.968,59.924-70.968,104.834c0,2.202,0.062,4.382,0.166,6.561h280.437 c0.104-2.179,0.166-4.359,0.166-6.561C393.009,103.907,364.406,64.756,322.041,43.983z M187.655,108.911 c-7.442,0-13.482-5.997-13.482-13.46c0-7.463,6.04-13.439,13.482-13.439c7.463,0,13.482,5.975,13.482,13.439 C201.137,102.914,195.097,108.911,187.655,108.911z M317.49,108.911c-7.442,0-13.482-5.997-13.482-13.46 c0-7.463,6.04-13.439,13.482-13.439c7.463,0,13.482,5.975,13.482,13.439C330.972,102.914,324.932,108.911,317.49,108.911z"
                />
                <path
                  d="M437.876,164.715h-1.251c-16.588,0-30.156,13.568-30.156,30.113v131.107 c0,16.61,13.59,30.134,30.156,30.134h1.273c16.609,0,30.113-13.568,30.113-30.134V194.827 C468.011,178.283,454.464,164.715,437.876,164.715z"
                />
              </svg>
              <span class="sr-only md:not-sr-only">Android</span>
            </a>
          {/if}

          {#if poster.iosLink}
            <a
              href={poster.iosLink}
              target="_blank"
              rel="noopener noreferrer"
              class="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600
              text-white text-sm md:text-base rounded-lg transition-all flex items-center gap-2 shadow-lg shadow-purple-500/20"
            >
              <svg
                class="w-4 h-4 md:w-5 md:h-5"
                viewBox="0 0 1000 1000"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"
                />
              </svg>
              <span class="sr-only md:not-sr-only">iOS</span>
            </a>
          {/if}
        </div>
      </div>
    </div>

    <!-- Image section - stacked below content on mobile -->
    <div class="w-full md:w-2/3 relative mt-4 md:mt-0">
      <div
        class="relative z-10 transform hover:scale-[1.02] transition-all duration-500"
      >
        <div
          class="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl blur opacity-30 group-hover:opacity-40 transition duration-1000"
        ></div>
        <div
          class="relative bg-white dark:bg-gray-900 rounded-2xl overflow-hidden shadow-xl border border-gray-200 dark:border-gray-800"
        >
          <!-- Only load media when they're active or adjacent to improve performance -->
          {#if isActive || direction === "next" || direction === "prev"}
            {#if isVideo}
              <video
                bind:this={mediaVideo}
                class="w-full h-auto object-contain max-h-[420px] md:max-h-[600px]"
                src={mediaPath}
                muted
                loop
                playsinline
                preload={isActive ? "auto" : "metadata"}
                aria-label={`${poster.text} showcase`}
              />
            {:else}
              <img
                loading="lazy"
                class="w-full h-auto object-contain max-h-[420px] md:max-h-[600px]"
                src={mediaPath}
                alt={`${poster.text} showcase`}
              />
            {/if}
          {/if}
        </div>
      </div>
    </div>
  </div>
</div>

<style>
  /* Mobile-specific adjustments */
  @media (max-width: 768px) {
    /* Optimize animations for mobile */
    div {
      will-change: transform, opacity;
    }

    /* Improve tap targets */
    a {
      min-height: 44px;
      min-width: 44px;
    }
  }
</style>
